import { ParseResult } from "papaparse";
import React, { useEffect, useState } from "react";
import IErrorDTO from "../../../models/DTO/IErrorDTO";
import IMaterialsBulkImportCsvDTO from "../../../models/DTO/IMaterialsBulkImportCsvDTO";
import IMaterialsBulkImportCsvProgressDTO from "../../../models/DTO/IMaterialsBulkImportCsvProgressDTO";
import IOnMaterialsBulkImportCsvDTO from "../../../models/DTO/IOnMaterialsBulkImportCsvDTO";
import EndpointsEnum from "../../../models/EndpointsEnum";
import IMaterialDataRow from "../../../models/IMaterialDataRow";
import INotification from "../../../models/INotification";
import IProcessedItemsDetails from "../../../models/IProcessedItemsDetails";
import ISupplierModel from "../../../models/ISupplierModel";
import IUserUI from "../../../models/IUserUI";
import NotificationLevelEnum from "../../../models/NotificationLevelEnum";
import NotificationPositionEnum from "../../../models/NotificationPositionEnum";
import OwButton from "../../shared/OwButton/OwButton";
import OwCsvReader from "../../shared/OwCsvReader/OwCsvReader";
import OwProcessedItemsDetails from "../../shared/OwProcessedItemsDetails/OwProcessedItemsDetails";
import OwProgressBar from "../../shared/OwProgressBar/OwProgressBar";
import OwTitle from "../../shared/OwTitle/OwTitle";
import "./MaterialsUploadCsvModal.scss";
import { Socket } from "socket.io-client";

type Props = {
    closeModalFn: (refresh?: boolean) => void;
    socket: Socket;
    notifyFn: (data: IErrorDTO | null, _notification?: any, isFormatted?: boolean) => void;
    userModel: IUserUI;
    supplierModel: ISupplierModel;
};

const MaterialsUploadCsvModal: React.FC<Props> = (props) => {
    const [csvData, setCsvData] = useState<ParseResult<IMaterialDataRow>>();
    const [isProcessing, setIsProcessing] = useState(false);
    const [file, setFile] = useState();
    const [processedItemDetails, setProcessedItemDetails] = useState<IProcessedItemsDetails | null>(null);
    const [progressDetails, setProgressDetails] = useState<{ progressPercent: number; progressCurrent: number; progressTotal: number }>({
        progressPercent: 0,
        progressCurrent: 0,
        progressTotal: 0,
    });

    useEffect(() => {
        props.socket.on(EndpointsEnum.MaterialOnBulkImportCSV, onMaterialsBulkImport);
        props.socket.on(EndpointsEnum.MaterialsBulkImportCsvProgress, onBulkImportCsvProgress);

        return () => {
            props.socket.removeListener(EndpointsEnum.MaterialOnBulkImportCSV, onMaterialsBulkImport);
            props.socket.removeListener(EndpointsEnum.MaterialsBulkImportCsvProgress, onBulkImportCsvProgress);
        };
    });

    const closeModal = () => {
        props.closeModalFn();
    };

    const csvLoaded = (_csvData?: ParseResult<IMaterialDataRow>) => {
        if (_csvData) {
            setCsvData(_csvData);
        }
    };

    const doUpload = () => {
        const rows = csvData?.data.map((dataRow: any) => {
            const rowDetail: IMaterialDataRow = {
                name: dataRow["Name"],
                qty_on_hand: dataRow["Qty. On Hand"],
                guid: dataRow["Guid"],
            };

            return rowDetail;
        });

        const payload: IMaterialsBulkImportCsvDTO = {
            userGuid: props.userModel.guid,
            userToken: props.userModel.token,
            supplierGuid: props.supplierModel.guid,
            rows: JSON.stringify(rows),
        };

        props.socket.emit(EndpointsEnum.MaterialBulkImportCSV, payload);
    };

    const onMaterialsBulkImport = (data: IOnMaterialsBulkImportCsvDTO) => {
        const notification: INotification = {
            message: "successfully_processed_materials_csv",
            level: NotificationLevelEnum.success,
            position: NotificationPositionEnum.bc,
        };

        props.notifyFn(null, notification);

        props.closeModalFn(true);
    };

    const dismissDetails = () => {};

    const onBulkImportCsvProgress = (data: IMaterialsBulkImportCsvProgressDTO) => {
        const progressPercent = (data.currentRow / data.totalRows) * 100;

        setProgressDetails({ progressCurrent: data.currentRow, progressTotal: data.totalRows, progressPercent });
    };

    return (
        <div className={"MaterialsUploadCsvModal p-2"}>
            <div className={"w-100"} style={{ minHeight: "2rem" }}>
                <div className={"d-flex justify-content-between"}>
                    <OwTitle leftIconName={"fad fa-cloud-upload-alt"} title={"upload_update"} tag={"h3"} />
                    <div>
                        <OwButton className={"btn-danger"} centerIconClass={"fas fa-times"} fn={closeModal} />
                    </div>
                </div>
            </div>

            <div className={"mt-4"}>
                <div className={"drop-area"}>
                    <OwCsvReader onFileLoaded={csvLoaded} isProcessing={isProcessing} value={file} />
                </div>
            </div>

            <div className={"mt-4"}>
                <OwButton title={"upload"} fn={doUpload} disabled={!csvData} />
            </div>

            <div>
                <OwProgressBar
                    currentPercent={progressDetails.progressPercent}
                    titleChunkCurrent={progressDetails.progressCurrent}
                    titleChunkTotal={progressDetails.progressTotal}
                    showTitleChunk={true}
                />
            </div>

            <div>
                <OwProcessedItemsDetails details={processedItemDetails} dismissProcessedDetails={dismissDetails} />
            </div>
        </div>
    );
};

export default MaterialsUploadCsvModal;
